import { RecurringRevenueCoverage } from '@interfaces/reports/recurringRevenue';
import { Population, resolvePopulation } from '@utils/resolvePopulation';

/**
 * Whether the dashboard shows the SUBSCRIPTION cards or falls back to the sales-only layout.
 *
 * Switching layout is an assertion - "you do not sell subscriptions" - and it removes the MRR, the at-risk
 * figure and any coverage note along with them. So the bar is not "is there evidence of a base" but "is there
 * evidence there is NOT one", and every ambiguous state has to keep the subscription layout.
 *
 * Three states reach `activeSubscribers === 0` while meaning entirely different things:
 *
 * - the producer genuinely sells no subscription. The only one the fallback is for.
 * - the request FAILED. The cards then say they could not load, which answers the question; switching layout
 *   erases the question instead.
 * - the response came back PARTIAL - money present, population missing - which is what version skew produces.
 *   Hiding it makes the strongest claim on the screen out of the weakest data on it, and takes the disclosure
 *   note down with the figure it was about. Codex, #267.
 *
 * Extracted from the page so it can be tested at all: nothing renders the dashboard page in the suite, and
 * a defect in this line is invisible until a producer with a real base sees the wrong screen.
 */
export const hasSubscriptionBase = ({
  hasLoaded,
  isLoading,
  hasFailed,
  snapshot,
  coverage,
}: {
  /** Whether a response was ever applied. Idle is not an answer about the business - see useRecurringRevenue. */
  hasLoaded: boolean;
  isLoading: boolean;
  hasFailed: boolean;
  snapshot: {
    activeSubscribers: number | null;
    mrr: number;
    mrrAtRisk: number;
  };
  coverage: RecurringRevenueCoverage;
}) => {
  if (isLoading) return false;
  if (hasFailed) return true;
  // Nothing was asked yet, or no community is selected. Deciding either layout from that is deciding from
  // nothing - and the subscription one would flicker away the moment the request starts. Codex, #267.
  if (!hasLoaded) return false;
  // Through the one door, so a payload that dropped the snapshot count but kept the coverage still counts as a
  // base: `coverage.current.total` is the same population, measured for the share's denominator.
  const population = resolvePopulation(snapshot, coverage);

  if (population.known && population.value > 0) return true;

  /**
   * An UNKNOWN population keeps the layout, for the same reason a failed request does.
   *
   * The fallback asserts "you do not sell subscriptions", and a response that never said how many there are
   * cannot support that. Only a count the report actually stated - at zero - can. Codex, #267.
   */
  if (!population.known) return true;

  /**
   * Any subscription money reported proves the base exists, whatever the population says.
   *
   * `uncoveredValue` counts here for the same reason: it is money the report holds OUT of the MRR, and money
   * cannot be omitted from a base that is not there.
   */
  const uncovered = coverage.current.uncoveredValue;

  /**
   * A BROKEN subscription figure keeps the layout too, and it is the layout that has to carry the warning.
   *
   * A negative MRR makes `resolveMrrCoverage` return `unusable` - the state that withholds the figure and names
   * the report as the broken part - but that note lives inside the subscription cards. With an empty reported
   * base and a malformed MRR, every signal below was false, the sales-only layout was chosen, and the warning
   * disappeared along with the figure it was about: a broken report rendered as a producer who sells no
   * subscriptions. Codex, #267.
   *
   * Absence is not malformation: `uncoveredValue` simply missing means the API is older, which says nothing
   * about a base and must not hold the layout on its own.
   *
   * A malformed `uncoveredValue` is deliberately NOT one of these signals, and the asymmetry is the point: a
   * layout is retained to carry a warning, so it may only be retained where a warning actually renders. With a
   * base reported empty and a valid MRR, the resolver says nothing about a broken uncovered amount - by an
   * earlier decision on this same PR, because declaring there would banner a community that is simply empty.
   * Holding the layout for a note that never mounts is an empty gesture. Where the base is NOT empty, the
   * resolver does diagnose it, and the population already holds the layout. Codex, #267.
   */
  const broken = (value: number) => !Number.isFinite(value) || value < 0;

  if (broken(snapshot.mrr) || broken(snapshot.mrrAtRisk)) return true;

  return (
    snapshot.mrr > 0 ||
    snapshot.mrrAtRisk > 0 ||
    (Number.isFinite(uncovered) && (uncovered as number) > 0)
  );
};

/**
 * Whether the base-movement block renders at all.
 *
 * A sibling of the rule above because it answers the same family of question - what the dashboard is entitled to
 * say about a subscription base - and because it was the third consumer to get the idle state wrong. Extracted
 * for the same reason: nothing in the suite renders the dashboard page, so a decision left inline there is a
 * decision nothing can check.
 *
 * The block is a claim about a PERIOD: who arrived, who left. It is hidden only when the report actually said
 * there is nothing to show - a base known to be empty, with no departures either. Idle and loading are not that,
 * and rendering "0 · ativos não informados" for a page nobody has asked anything of is the failure this closes.
 */
export const showsBaseMovement = ({
  hasLoaded,
  isLoading,
  hasFailed,
  population,
  churnedSubscribers,
}: {
  hasLoaded: boolean;
  isLoading: boolean;
  hasFailed: boolean;
  population: Population;
  churnedSubscribers: number;
}) => {
  if (hasFailed) return true;
  if (isLoading) return true;
  if (!hasLoaded) return false;

  const knownEmpty = population.known && population.value === 0;

  return !(knownEmpty && !churnedSubscribers);
};
